// src/features/appraisal/PerformanceAppraisalPage.jsx
import React, {useState, useEffect, useMemo} from 'react';
import { getAuthHeader } from '../../utils/auth';
import { useAuth } from './../auth/AuthContext';
import { useDataEngine } from '@dhis2/app-runtime';
import { CircularLoader, NoticeBox } from '@dhis2/ui';
import { useParams, useSearchParams } from 'react-router-dom';
import AppraisalForm from './AppraisalForm';
import { Users, ChevronLeft, UserCircle } from 'lucide-react';
import './AppraisalForm.css';
import { useCurrentUserPosition } from './hooks/useCurrentUserPosition';
import { useAppraisalEvents } from './hooks/useAppraisalEvents';
import {
  APPRAISAL_PROGRAM_STAGES,
  APPRAISAL_PROGRAM,
  DATA_ELEMENTS,
  APPRAISAL_STATUSES,
} from './api/appraisalConstants';
import { POSITION_ATTRIBUTES } from '../auth/utils/reportingStructure';

const extractAttributes = (attributes = []) => {
  const map = {};
  attributes.forEach((attr) => (map[attr.attribute] = attr.value));
  return map;
};

const PerformanceAppraisalPage = () => {
  // ✅ All hooks at the very top
  const { user } = useAuth();
  const { type } = useParams();
  const [searchParams] = useSearchParams();
  const [subordinates, setSubordinates] = useState([]);
  const subordinateTei = searchParams.get('subordinate'); 
  const engine = useDataEngine();

  // Load position for current user (manager or employee)
  const { currentUserPosition, reportingToPosition, loading: positionLoading } =
    useCurrentUserPosition(user?.id, user?.organisationUnits || []);

  // ✅ Validate type
  const isValidType = type === 'mid-year' || type === 'year-end';
  const programStage = isValidType
    ? type === 'mid-year'
      ? APPRAISAL_PROGRAM_STAGES.MID_YEAR
      : APPRAISAL_PROGRAM_STAGES.YEAR_END
    : null;

  // ✅ Determine TARGET TEI: subordinate (if viewing team) OR own position
  const targetTei = subordinateTei || currentUserPosition?.trackedEntityInstance || null;
  const targetOrgUnit = currentUserPosition?.orgUnit || null;

   const selectedSubordinate = useMemo(
      () => subordinates.find(sub => sub.tei === subordinateTei),
      [subordinates, subordinateTei]
   );
  
  
    useEffect(() => {
      if (!user?.id || !currentUserPosition?.trackedEntityInstance) return;
      
      const loadSubordinates = async () => {
        try {
          // Step 1: Query positions where REPORTING_TO = manager's position TEI
          const response = await engine.query({
            subordinates: {
              resource: 'trackedEntityInstances',
              params: {
                trackedEntityType: 'UGGo0Ebjl0E',
                ou: currentUserPosition?.orgUnit,
                ouMode: 'ACCESSIBLE',
                filter: `${POSITION_ATTRIBUTES.REPORTING_TO}:eq:${currentUserPosition.trackedEntityInstance}`,
                fields: 'trackedEntityInstance,attributes[attribute,value]'
              }
            }
          });
          
          // Step 2: Map positions with incumbent info
          const subs = response.subordinates?.trackedEntityInstances?.map(tei => {
            const attrs = extractAttributes(tei.attributes || []);
            return {
              tei: tei.trackedEntityInstance,
              positionTitle: attrs[POSITION_ATTRIBUTES.POSITION_TITLE] || 'Unknown Position',
              incumbentId: attrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT]
            };
          }) || [];

          // Step 3: Fetch user names from /api/users endpoint
          const subsWithNames = await Promise.all(subs.map(async sub => {
            if (sub.incumbentId) {
              try {
    
                const userRes = await fetch(`${window.location.origin}/api/users/${sub.incumbentId}`, {
                  headers: {
                    ...getAuthHeader(),  
                    'Content-Type': 'application/json'
                  }
                });
                
                if (userRes.ok) {
                  const userData = await userRes.json();
                  return {
                    ...sub,
                    displayName: userData.displayName || userData.name || sub.incumbentId
                  };
                }
              } catch (err) {
                console.warn('Failed to fetch user name for', sub.incumbentId, err);
              }
            }
            // Fallback: Use position title if name fetch fails
            return { ...sub, displayName: sub.positionTitle };
          }));
          
          console.log('👥 Subordinates loaded:', subsWithNames);
          setSubordinates(subsWithNames);
        } catch (err) {
          console.error('Failed to load subordinates:', err);
          setSubordinates([]);
        }
      };

      loadSubordinates();
    }, [user?.id, currentUserPosition, engine]);
  
  // ✅ Load appraisal event for TARGET TEI (critical: use targetTei, not positionTEI)
  const { appraisalEvent, loading: eventLoading, refetch } = useAppraisalEvents(
    targetTei,              // ← Use targetTei (subordinate or self)
    programStage,
    targetOrgUnit,          // ← Use targetOrgUnit
    engine
  );

  // UI States

  if (!isValidType) {
    return (
      <div className="apr-page-wrap">
        <NoticeBox error title="Invalid Appraisal Type">
          Please select a valid appraisal type:
          <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
            <li><a href="/performance-appraisal/mid-year">Mid-Year Appraisal</a></li>
            <li><a href="/performance-appraisal/year-end">Year-End Appraisal</a></li>
          </ul>
        </NoticeBox>
      </div>
    );
  }

  if (positionLoading) {
    return (
      <div className="apr-loading">
        <CircularLoader small />
        <span>Loading your position…</span>
      </div>
    );
  }

  if (!currentUserPosition) {
    return (
      <div className="apr-page-wrap">
        <div className="apr-no-position">
          <h4><span>⚠️</span> No Position Assigned</h4>
          <p>Your account (<strong>{user?.username}</strong>) is not linked to any position in the system.</p>
          <p><strong>Name:</strong> {user?.name}</p>
          <p><strong>Organisation:</strong> {user?.organisationUnits?.map(ou => ou.name).join(', ') || 'None'}</p>
        </div>
      </div>
    );
  }

  if (eventLoading) {
    return (
      <div className="apr-loading">
        <CircularLoader small />
        <span>Loading appraisal data…</span>
      </div>
    );
  }

  const isMidYear = type === 'mid-year';
  //const selectedSubordinate = subordinates.find(sub => sub.tei === subordinateTei);
 

  // ✅ Render form with TARGET TEI (subordinate or self)
  return (
    <div className="apr-page-wrap">

      {/* ── Page Top Bar ──────────────────────────────────────── */}
      <div className="apr-page-top">
        <div>
          <h2 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 700, color: 'var(--apr-text)' }}>
            {isMidYear ? 'Mid-Year' : 'Year-End'} Performance Appraisal
          </h2>
          <div style={{ fontSize: '0.85rem', color: 'var(--apr-text-muted)', marginTop: '0.2rem' }}>
            {subordinateTei
              ? `Reviewing: ${selectedSubordinate?.displayName || selectedSubordinate?.positionTitle || 'Team Member'}`
              : 'Reviewing your own appraisal'}
          </div>
        </div>
        <span className={`apr-type-badge ${isMidYear ? 'apr-type-badge--mid' : 'apr-type-badge--year'}`}>
          {isMidYear ? '🗓 Mid-Year' : '📅 Year-End'}
        </span>
      </div>

      {/* ── Subordinate Selector ─────────────────────────────── */}
      {subordinates.length > 0 && (
        <div className="apr-selector-bar">
          <span className="apr-selector-icon"><Users size={18} /></span>
          <label className="apr-selector-label" htmlFor="subordinate-select">Review Team Member:</label>
         <select 
            value={subordinateTei || ''}
            onChange={(e) => {
              const tei = e.target.value;
              if (tei) {
                window.history.replaceState({}, '', `?subordinate=${tei}`);
              } else {
                window.history.replaceState({}, '', window.location.pathname);
              }
              window.location.reload();
            }}
            style={{ padding: 4, borderRadius: 4 }}
          >
            <option value="">-- Select team member --</option>
            {subordinates.map(sub => (
              <option key={sub.tei} value={sub.tei}>
                {sub.displayName} - {sub.positionTitle}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* ── Context Banner (when viewing a subordinate) ───────── */}
      {subordinateTei && selectedSubordinate && (
          <div className="apr-context-banner">
            <span className="apr-context-banner-icon"><UserCircle size={20} /></span>
            <div>
              <div>
                Viewing appraisal for{' '}
                <span className="apr-context-banner-name">
                  {selectedSubordinate.displayName || selectedSubordinate.positionTitle || 'Team Member'}
                </span>
              </div>
              {selectedSubordinate.positionTitle && selectedSubordinate.displayName && (
                <div className="apr-context-banner-id">
                  Position: {selectedSubordinate.positionTitle}
                </div>
              )}
            </div>
            <button
              className="apr-back-link"
              onClick={() => {
                window.history.replaceState({}, '', window.location.pathname);
                window.location.reload();
              }}
              style={{ 
                  boxShadow: "0 8px 10px rgba(108, 105, 105, 0.5)",
                  color: 'white',
                  backgroundColor: 'rgb(228, 138, 3)',
                  borderRadius: '8px',
                  border: 'none',
                  width: '170px',
                  height: '35px',
                  cursor: 'pointer' }}
            >
              <ChevronLeft size={14} />
              Back to my appraisal
            </button>
          </div>
        )}

      {/* ── Appraisal Form ───────────────────────────────────── */}
      <AppraisalForm
        positionTEI={targetTei}
        programStage={programStage}
        orgUnit={targetOrgUnit}
        //reportingToPosition={reportingToPosition}
        appraisalEvent={appraisalEvent}
        appraisalProgram={APPRAISAL_PROGRAM}
        dataElements={DATA_ELEMENTS}
        appraisalStatusOptions={APPRAISAL_STATUSES}
        onSaved={refetch}
      />
    </div>
  );
};

export default PerformanceAppraisalPage;